import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KanbanRepository } from '../../data/repositories/KanbanRepository';
import { ResponsiblesRepository } from '../../data/repositories/ResponsiblesRepository';
import {
  formatHabitaDateInput,
  HabitaButton,
  HabitaCheckbox,
  HabitaDateRangeFilter,
  HabitaErrorNotice,
  HabitaSearchInput,
  HabitaSelect,
  HabitaTextInput,
  isHabitaDateInputValue,
  resolveHabitaDateRange,
} from '../../shared/components/habita';
import type { HabitaDatePeriod, HabitaDateRangeFilterValue } from '../../shared/components/habita';
import {
  PASTA_STATUS_DESCRIPTIONS,
  PASTA_STATUS_LABELS,
  PASTA_STATUS_ORDER,
} from '../../shared/habitaStatusPresentation';
import type {
  HabitaProcessListItem,
  HabitaResponsible,
  KanbanColumnData,
  PastaStatus,
} from '../../shared/models';
import {
  DEFAULT_PROCESS_SORT_ORDER,
  processSortOrderLabel,
  type ProcessSortOrder,
} from '../../shared/utils/processOrdering';
import {
  getSdrResponsibleEmail,
  isSdrScopedUser,
} from '../../shared/utils';
import {
  KanbanBoard,
  KanbanEmptyState,
  KanbanGlobalLoading,
  KanbanSummaryPill,
} from './components/KanbanWidgets';

const KANBAN_PAGE_SIZE = 20;
const KANBAN_FILTERS_STORAGE_KEY = 'habita4u_kanban_filters';
const KANBAN_FILTERS_STORAGE_VERSION = 2;
const DEFAULT_KANBAN_VISIBLE_STATUSES: PastaStatus[] = [
  'COLETA_DADOS',
  'RECEBIMENTO_DOCS',
  'COLETA_DADOS_COMPLEMENTARES',
  'VALIDACAO_PASTA',
];

const dateFilterPeriods: HabitaDatePeriod[] = [
  'today',
  'yesterday',
  '7d',
  '30d',
  '90d',
  'month',
  'previous_month',
  'previous_quarter',
  'year',
  'custom',
];

const defaultDateFilterValue: HabitaDateRangeFilterValue = {
  customEnd: '',
  customStart: '',
  period: '30d',
};

interface PersistedKanbanFilters {
  dateFilter: HabitaDateRangeFilterValue;
  version?: number;
  processoTerm: string;
  responsavel: string;
  searchTerm: string;
  sortOrder: ProcessSortOrder;
  visibleStatuses: PastaStatus[];
}

const defaultKanbanFilters: PersistedKanbanFilters = {
  dateFilter: defaultDateFilter(),
  version: KANBAN_FILTERS_STORAGE_VERSION,
  processoTerm: '',
  responsavel: '',
  searchTerm: '',
  sortOrder: DEFAULT_PROCESS_SORT_ORDER,
  visibleStatuses: [...DEFAULT_KANBAN_VISIBLE_STATUSES],
};

function defaultDateFilter(): HabitaDateRangeFilterValue {
  return { ...defaultDateFilterValue };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function persistedString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function persistedSortOrder(value: unknown): ProcessSortOrder {
  return value === 'asc' || value === 'desc' ? value : DEFAULT_PROCESS_SORT_ORDER;
}

function persistedVisibleStatuses(value: unknown): PastaStatus[] {
  if (!Array.isArray(value)) return [...DEFAULT_KANBAN_VISIBLE_STATUSES];

  const selectedStatusSet = new Set(
    value.filter((status): status is PastaStatus => PASTA_STATUS_ORDER.includes(status as PastaStatus))
  );
  const selectedStatuses = PASTA_STATUS_ORDER.filter((status) => selectedStatusSet.has(status));

  return selectedStatuses.length > 0 ? selectedStatuses : [...DEFAULT_KANBAN_VISIBLE_STATUSES];
}

function persistedDateFilter(value: unknown): HabitaDateRangeFilterValue {
  if (!isRecord(value)) return defaultDateFilter();

  const savedPeriod = value.period;
  if (!dateFilterPeriods.includes(savedPeriod as HabitaDatePeriod)) return defaultDateFilter();

  if (savedPeriod === 'custom') {
    const customStart = value.customStart;
    const customEnd = value.customEnd;
    if (!isHabitaDateInputValue(customStart) || !isHabitaDateInputValue(customEnd) || customStart > customEnd) {
      return defaultDateFilter();
    }

    return {
      customEnd,
      customStart,
      period: 'custom',
    };
  }

  return {
    customEnd: '',
    customStart: '',
    period: savedPeriod as HabitaDatePeriod,
  };
}

function hasDefaultVisibleStatuses(statuses: PastaStatus[]): boolean {
  if (statuses.length !== DEFAULT_KANBAN_VISIBLE_STATUSES.length) return false;

  const statusSet = new Set(statuses);
  return DEFAULT_KANBAN_VISIBLE_STATUSES.every((status) => statusSet.has(status));
}

function readPersistedKanbanFilters(): PersistedKanbanFilters {
  if (typeof window === 'undefined') return defaultKanbanFilters;

  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(KANBAN_FILTERS_STORAGE_KEY) ?? 'null');
    if (!isRecord(parsed)) return defaultKanbanFilters;

    const shouldUsePersistedVisibleStatuses = parsed.version === KANBAN_FILTERS_STORAGE_VERSION;

    return {
      dateFilter: persistedDateFilter(parsed.dateFilter),
      version: KANBAN_FILTERS_STORAGE_VERSION,
      processoTerm: persistedString(parsed.processoTerm),
      responsavel: persistedString(parsed.responsavel),
      searchTerm: persistedString(parsed.searchTerm),
      sortOrder: persistedSortOrder(parsed.sortOrder),
      visibleStatuses: shouldUsePersistedVisibleStatuses
        ? persistedVisibleStatuses(parsed.visibleStatuses)
        : [...DEFAULT_KANBAN_VISIBLE_STATUSES],
    };
  } catch {
    return defaultKanbanFilters;
  }
}

function persistKanbanFilters(filters: PersistedKanbanFilters) {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(
      KANBAN_FILTERS_STORAGE_KEY,
      JSON.stringify({ ...filters, version: KANBAN_FILTERS_STORAGE_VERSION })
    );
  } catch {
  }
}

function responsibleOptionLabel(responsible: HabitaResponsible): string {
  return responsible.name || responsible.email;
}

function buildResponsibleOptions(
  responsibles: HabitaResponsible[],
  selectedResponsible: string
): { label: string; value: string }[] {
  const options = responsibles
    .filter((responsible) => responsible.email || responsible.name)
    .map((responsible) => ({
      label: responsibleOptionLabel(responsible),
      value: responsible.email || responsible.name,
    }));

  if (
    selectedResponsible.trim() &&
    !options.some((option) => option.value === selectedResponsible)
  ) {
    options.push({ label: selectedResponsible, value: selectedResponsible });
  }

  return options;
}

interface KanbanColumnPageState {
  page: number;
  total: number;
  hasExplicitTotal: boolean;
  hasNextPage: boolean;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

type KanbanColumnPageStateMap = Record<PastaStatus, KanbanColumnPageState>;

function createEmptyColumns(): KanbanColumnData[] {
  return PASTA_STATUS_ORDER.map((status) => ({
    status,
    label: PASTA_STATUS_LABELS[status],
    description: PASTA_STATUS_DESCRIPTIONS[status],
    items: [],
  }));
}

function createInitialColumnPageState(loadingStatuses: readonly PastaStatus[] = []): KanbanColumnPageStateMap {
  const loadingStatusSet = new Set(loadingStatuses);

  return PASTA_STATUS_ORDER.reduce((acc, status) => {
    acc[status] = {
      page: 0,
      total: 0,
      hasExplicitTotal: false,
      hasNextPage: false,
      loading: loadingStatusSet.has(status),
      error: null,
      initialized: false,
    };
    return acc;
  }, {} as KanbanColumnPageStateMap);
}

function mergeItems(
  currentItems: HabitaProcessListItem[],
  nextItems: HabitaProcessListItem[]
): HabitaProcessListItem[] {
  const seenIds = new Set(currentItems.map((item) => String(item.id)));
  const uniqueNextItems = nextItems.filter((item) => {
    const id = String(item.id);
    if (seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });

  return [...currentItems, ...uniqueNextItems];
}

function KanbanColumnSelector({
  selectedStatuses,
  onSelectedStatusesChange,
}: {
  selectedStatuses: PastaStatus[];
  onSelectedStatusesChange: (statuses: PastaStatus[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedStatusSet = useMemo(() => new Set(selectedStatuses), [selectedStatuses]);
  const selectedCount = selectedStatuses.length;
  const allSelected = selectedCount === PASTA_STATUS_ORDER.length;

  useEffect(() => {
    if (!isOpen) return undefined;

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  function toggleStatus(status: PastaStatus, checked: boolean) {
    if (!checked && selectedStatuses.length === 1 && selectedStatusSet.has(status)) {
      return;
    }

    const nextSelectedStatusSet = new Set(selectedStatuses);
    if (checked) {
      nextSelectedStatusSet.add(status);
    } else {
      nextSelectedStatusSet.delete(status);
    }

    onSelectedStatusesChange(
      PASTA_STATUS_ORDER.filter((pastaStatus) => nextSelectedStatusSet.has(pastaStatus))
    );
  }

  function selectAllStatuses() {
    onSelectedStatusesChange([...PASTA_STATUS_ORDER]);
  }

  return (
    <div ref={rootRef} className="relative">
      <HabitaButton
        icon="ri-layout-column-line"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        Colunas
        <span className="rounded bg-accent px-1.5 py-0.5 text-[11px] text-textSecondary">
          {selectedCount}/{PASTA_STATUS_ORDER.length}
        </span>
      </HabitaButton>

      {isOpen && (
        <div className="absolute right-0 z-30 mt-2 w-72 overflow-hidden rounded-lg border border-border-subtle bg-card shadow-lg">
          <div className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
            <span className="font-instrument text-xs font-semibold uppercase tracking-wide text-textSecondary">
              Colunas
            </span>
            <HabitaButton
              disabled={allSelected}
              onClick={selectAllStatuses}
              size="sm"
              variant="ghost"
            >
              Todas
            </HabitaButton>
          </div>

          <div className="max-h-80 overflow-y-auto py-1">
            {PASTA_STATUS_ORDER.map((status) => {
              const checked = selectedStatusSet.has(status);
              const isOnlySelected = checked && selectedStatuses.length === 1;

              return (
                <HabitaCheckbox
                  key={status}
                  checked={checked}
                  onCheckedChange={(nextChecked) => toggleStatus(status, nextChecked)}
                  className={isOnlySelected ? 'opacity-75' : undefined}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-textPrimary">
                      {PASTA_STATUS_LABELS[status]}
                    </span>
                    <span className="mt-0.5 line-clamp-1 block text-xs text-textSecondary">
                      {PASTA_STATUS_DESCRIPTIONS[status]}
                    </span>
                  </span>
                </HabitaCheckbox>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Kanban() {
  const kanbanRepository = useMemo(() => new KanbanRepository(), []);
  const responsiblesRepository = useMemo(() => new ResponsiblesRepository(), []);
  const persistedFilters = useMemo(() => readPersistedKanbanFilters(), []);
  const isResponsibleScoped = useMemo(() => isSdrScopedUser(), []);
  const responsibleScopeEmail = useMemo(() => getSdrResponsibleEmail(), []);
  const [columns, setColumns] = useState<KanbanColumnData[]>(() => createEmptyColumns());
  const [responsibles, setResponsibles] = useState<HabitaResponsible[]>([]);
  const [columnPageState, setColumnPageState] = useState<KanbanColumnPageStateMap>(() => createInitialColumnPageState(persistedFilters.visibleStatuses));
  const [searchTerm, setSearchTerm] = useState(persistedFilters.searchTerm);
  const [processoTerm, setProcessoTerm] = useState(persistedFilters.processoTerm);
  const [responsavel, setResponsavel] = useState(isResponsibleScoped ? '' : persistedFilters.responsavel);
  const [sortOrder, setSortOrder] = useState<ProcessSortOrder>(persistedFilters.sortOrder);
  const [visibleStatuses, setVisibleStatuses] = useState<PastaStatus[]>(persistedFilters.visibleStatuses);
  const [dateFilter, setDateFilter] = useState<HabitaDateRangeFilterValue>(persistedFilters.dateFilter);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(persistedFilters.searchTerm);
  const [debouncedProcessoTerm, setDebouncedProcessoTerm] = useState(persistedFilters.processoTerm);
  const [debouncedResponsavel, setDebouncedResponsavel] = useState(isResponsibleScoped ? responsibleScopeEmail ?? '' : persistedFilters.responsavel);
  const [error, setError] = useState<string | null>(null);
  const [isBoardLoading, setIsBoardLoading] = useState(true);
  const mountedRef = useRef(false);
  const columnPageStateRef = useRef(columnPageState);
  const visibleStatusesRef = useRef(visibleStatuses);
  const requestSequenceRef = useRef<Record<PastaStatus, number>>(
    PASTA_STATUS_ORDER.reduce((acc, status) => {
      acc[status] = 0;
      return acc;
    }, {} as Record<PastaStatus, number>)
  );
  const requestRange = useMemo(() => resolveHabitaDateRange(dateFilter), [dateFilter]);
  const maxSelectableDate = useMemo(() => formatHabitaDateInput(new Date()), []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    visibleStatusesRef.current = visibleStatuses;
  }, [visibleStatuses]);

  useEffect(() => {
    if (isResponsibleScoped) {
      setResponsibles([]);
      return undefined;
    }

    let cancelled = false;

    async function loadResponsibles() {
      try {
        const nextResponsibles = await responsiblesRepository.fetchResponsibles();
        if (!cancelled) {
          setResponsibles(nextResponsibles);
        }
      } catch {
        if (!cancelled) {
          setResponsibles([]);
        }
      }
    }

    void loadResponsibles();

    return () => {
      cancelled = true;
    };
  }, [isResponsibleScoped, responsiblesRepository]);

  useEffect(() => {
    persistKanbanFilters({
      dateFilter: {
        customEnd: dateFilter.period === 'custom' ? dateFilter.customEnd : '',
        customStart: dateFilter.period === 'custom' ? dateFilter.customStart : '',
        period: dateFilter.period,
      },
      processoTerm,
      responsavel: isResponsibleScoped ? '' : responsavel,
      searchTerm,
      sortOrder,
      visibleStatuses,
    });
  }, [dateFilter.customEnd, dateFilter.customStart, dateFilter.period, isResponsibleScoped, processoTerm, responsavel, searchTerm, sortOrder, visibleStatuses]);

  const updateColumnPageState = useCallback((
    status: PastaStatus,
    updater: (current: KanbanColumnPageState) => KanbanColumnPageState
  ) => {
    const currentState = columnPageStateRef.current;
    const nextState = {
      ...currentState,
      [status]: updater(currentState[status]),
    };
    columnPageStateRef.current = nextState;
    setColumnPageState(nextState);
  }, []);

  const loadColumnPage = useCallback(async (
    status: PastaStatus,
    pageToLoad: number,
    mode: 'replace' | 'append'
  ): Promise<boolean> => {
    const currentState = columnPageStateRef.current[status];
    if (mode === 'append' && (currentState.loading || !currentState.hasNextPage)) {
      return false;
    }

    const requestId = (requestSequenceRef.current[status] ?? 0) + 1;
    requestSequenceRef.current[status] = requestId;
    updateColumnPageState(status, (current) => ({
      ...current,
      loading: true,
      error: null,
    }));

    try {
      if (isResponsibleScoped && !responsibleScopeEmail) {
        updateColumnPageState(status, (current) => ({
          ...current,
          loading: false,
          error: 'Não foi possível identificar o responsável do usuário logado.',
          initialized: true,
        }));
        return false;
      }

      const requestedResponsibleEmail = isResponsibleScoped
        ? responsibleScopeEmail ?? ''
        : debouncedResponsavel.trim();
      const result = await kanbanRepository.fetchKanbanColumnPage({
        status,
        page: pageToLoad,
        page_size: KANBAN_PAGE_SIZE,
        startDate: requestRange.startDate,
        endDate: requestRange.endDate,
        ...(debouncedSearchTerm.trim() ? { searchText: debouncedSearchTerm } : {}),
        ...(debouncedProcessoTerm.trim() ? { processoText: debouncedProcessoTerm } : {}),
        ...(requestedResponsibleEmail ? { responsibleEmail: requestedResponsibleEmail } : {}),
        sortOrder,
      });

      if (!mountedRef.current || requestSequenceRef.current[status] !== requestId) {
        return false;
      }

      setColumns((currentColumns) =>
        currentColumns.map((column) =>
          column.status === status
            ? {
                ...column,
                items: mode === 'replace'
                  ? result.items
                  : mergeItems(column.items, result.items),
              }
            : column
        )
      );
      updateColumnPageState(status, (current) => ({
        ...current,
        page: pageToLoad,
        total: result.hasExplicitTotal
          ? result.total
          : mode === 'replace'
            ? result.items.length
            : current.total + result.items.length,
        hasExplicitTotal: result.hasExplicitTotal,
        hasNextPage: result.hasNextPage,
        loading: false,
        error: null,
        initialized: true,
      }));

      return true;
    } catch (loadError) {
      console.error(`Erro ao carregar coluna ${status} do Kanban:`, loadError);
      if (mountedRef.current && requestSequenceRef.current[status] === requestId) {
        updateColumnPageState(status, (current) => ({
          ...current,
          loading: false,
          error: 'Não foi possível carregar esta coluna.',
          initialized: true,
        }));
      }
      return false;
    }
  }, [
    debouncedProcessoTerm,
    debouncedResponsavel,
    debouncedSearchTerm,
    isResponsibleScoped,
    kanbanRepository,
    responsibleScopeEmail,
    requestRange.endDate,
    requestRange.startDate,
    sortOrder,
    updateColumnPageState,
  ]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setDebouncedProcessoTerm(processoTerm);
      setDebouncedResponsavel(isResponsibleScoped ? responsibleScopeEmail ?? '' : responsavel);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [isResponsibleScoped, processoTerm, responsavel, responsibleScopeEmail, searchTerm]);

  useEffect(() => {
    let cancelled = false;
    const statusesToLoad = visibleStatusesRef.current;
    const initialColumnPageState = createInitialColumnPageState(statusesToLoad);

    setError(null);
    setIsBoardLoading(true);
    setColumns(createEmptyColumns());
    columnPageStateRef.current = initialColumnPageState;
    setColumnPageState(initialColumnPageState);

    if (isResponsibleScoped && !responsibleScopeEmail) {
      setError('Não foi possível identificar o responsável do usuário logado.');
      setIsBoardLoading(false);
      return () => {
        cancelled = true;
      };
    }

    async function loadInitialColumns() {
      const results = await Promise.all(
        statusesToLoad.map((status) => loadColumnPage(status, 1, 'replace'))
      );

      if (cancelled || !mountedRef.current) return;

      if (results.length > 0 && results.every((loaded) => !loaded)) {
        setError('Não foi possível carregar os dados do Kanban.');
      }

      setIsBoardLoading(false);
    }

    void loadInitialColumns();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm, isResponsibleScoped, loadColumnPage, responsibleScopeEmail, sortOrder]);

  useEffect(() => {
    if (isResponsibleScoped && !responsibleScopeEmail) return;

    visibleStatuses.forEach((status) => {
      const currentState = columnPageStateRef.current[status];
      if (!currentState.initialized && !currentState.loading) {
        void loadColumnPage(status, 1, 'replace');
      }
    });
  }, [isResponsibleScoped, loadColumnPage, responsibleScopeEmail, visibleStatuses]);

  const loadMoreColumn = useCallback((status: PastaStatus) => {
    const currentState = columnPageStateRef.current[status];
    if (!currentState || currentState.loading) return;

    if (currentState.error) {
      const hasLoadedAnyPage = currentState.page > 0;
      void loadColumnPage(
        status,
        hasLoadedAnyPage ? currentState.page + 1 : 1,
        hasLoadedAnyPage ? 'append' : 'replace'
      );
      return;
    }

    if (!currentState.hasNextPage) return;

    void loadColumnPage(status, currentState.page + 1, 'append');
  }, [loadColumnPage]);

  const visibleStatusSet = useMemo(() => new Set(visibleStatuses), [visibleStatuses]);
  const displayedColumns = useMemo(
    () => columns.filter((column) => visibleStatusSet.has(column.status)),
    [columns, visibleStatusSet]
  );

  const responsaveis = useMemo(
    () => isResponsibleScoped ? [] : buildResponsibleOptions(responsibles, responsavel),
    [isResponsibleScoped, responsibles, responsavel]
  );

  const hasFilters =
    searchTerm.trim() !== '' ||
    processoTerm.trim() !== '' ||
    (!isResponsibleScoped && responsavel.trim() !== '') ||
    dateFilter.period !== '30d' ||
    sortOrder !== DEFAULT_PROCESS_SORT_ORDER ||
    !hasDefaultVisibleStatuses(visibleStatuses);
  const statusCount = useCallback((status: PastaStatus) => {
    const column = columns.find((c) => c.status === status);
    const pageState = columnPageState[status];
    if (pageState.initialized && pageState.hasExplicitTotal) {
      return pageState.total;
    }

    return column?.items.length ?? 0;
  }, [columnPageState, columns]);

  const totalClientes = visibleStatuses.reduce((total, status) => total + statusCount(status), 0);
  const emValidacao =
    (visibleStatusSet.has('VALIDACAO_PASTA') ? statusCount('VALIDACAO_PASTA') : 0) +
    (visibleStatusSet.has('PRE_ANALISE_CREDITO') ? statusCount('PRE_ANALISE_CREDITO') : 0);
  const ajusteDocumental = visibleStatusSet.has('AJUSTE') ? statusCount('AJUSTE') : 0;
  const aprovadas =
    (visibleStatusSet.has('APROVADO') ? statusCount('APROVADO') : 0) +
    (visibleStatusSet.has('CONDICIONADO') ? statusCount('CONDICIONADO') : 0);

  function clearFilters() {
    setSearchTerm('');
    setProcessoTerm('');
    setResponsavel('');
    setSortOrder(DEFAULT_PROCESS_SORT_ORDER);
    setDebouncedSearchTerm('');
    setDebouncedProcessoTerm('');
    setDebouncedResponsavel(isResponsibleScoped ? responsibleScopeEmail ?? '' : '');
    setVisibleStatuses([...DEFAULT_KANBAN_VISIBLE_STATUSES]);
    setDateFilter(defaultDateFilter());
  }

  return (
    <section className="w-full space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-instrument text-sm text-textSecondary">
          Visualize o fluxo operacional das pastas em tempo real.
        </p>
        <div className="flex flex-wrap gap-2">
          <KanbanSummaryPill label="Total" value={totalClientes} />
          <KanbanSummaryPill label="Em validação" value={emValidacao} tone="warning" />
          <KanbanSummaryPill label="Ajuste documental" value={ajusteDocumental} tone="warning" />
          <KanbanSummaryPill label="Aprovadas" value={aprovadas} tone="success" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <HabitaSearchInput
          value={searchTerm}
          onValueChange={setSearchTerm}
          placeholder="Buscar por cliente, CPF ou documento"
          containerClassName="min-w-[220px] max-w-md flex-1"
          inputClassName="h-9 bg-card text-textPrimary"
        />
        <HabitaDateRangeFilter
          value={dateFilter}
          onApply={setDateFilter}
          onClear={() => setDateFilter(defaultDateFilter())}
          maxDateInput={maxSelectableDate}
        />
        <HabitaTextInput
          value={processoTerm}
          onChange={(event) => setProcessoTerm(event.target.value)}
          placeholder="Processo"
          containerClassName="w-40"
        />
        {!isResponsibleScoped && (
          <HabitaSelect
            value={responsavel}
            onChange={(event) => setResponsavel(event.target.value)}
            options={[
              { label: 'Responsável', value: '' },
              ...responsaveis,
            ]}
            className="w-48"
            aria-label="Responsável"
          />
        )}
        <KanbanColumnSelector
          selectedStatuses={visibleStatuses}
          onSelectedStatusesChange={setVisibleStatuses}
        />
        <HabitaButton
          icon={sortOrder === 'desc' ? 'ri-sort-desc' : 'ri-sort-asc'}
          onClick={() => setSortOrder((current) => current === 'desc' ? 'asc' : 'desc')}
          size="sm"
        >
          {processSortOrderLabel(sortOrder)}
        </HabitaButton>
        {hasFilters && (
          <HabitaButton icon="ri-close-line" onClick={clearFilters} size="sm" variant="ghost">
            Limpar
          </HabitaButton>
        )}
      </div>

      {error && (
        <HabitaErrorNotice>{error}</HabitaErrorNotice>
      )}

      {isBoardLoading ? (
        <KanbanGlobalLoading />
      ) : displayedColumns.length === 0 ? (
        <KanbanEmptyState />
      ) : (
        <KanbanBoard
          columns={displayedColumns}
          columnPageState={columnPageState}
          hasFilters={hasFilters}
          onLoadMoreColumn={loadMoreColumn}
        />
      )}
    </section>
  );
}
